import { BASE_LOCALE_CODE } from '@/constant/profileLinkLocales';

/**
 * 프로필링크 블록의 언어별 번역·노출 규칙.
 *
 * 원본(한국어)은 block_content 의 기존 필드가 그대로 내용이 된다.
 * 다른 언어는 block_content.i18n[lang] 에 덮어쓸 값만 넣는다 — 비어 있으면 원본이 그대로 보인다.
 * 특정 언어에서 블록을 감추려면 block_content.hidden_langs 에 그 언어 코드를 넣는다.
 *
 *   block_content: {
 *       title: '여름 세일',                                  // 원본(ko)
 *       link_url: 'https://…',
 *       i18n: {
 *           ja: { title: 'サマーセール' },                    // 제목만 번역, 링크는 원본 공용
 *           en: { title: 'Summer Sale', link_url: 'https://us…' },
 *       },
 *       hidden_langs: ['zh'],                               // 중국 계정에서는 이 블록을 감춘다
 *   }
 *
 * hidden_langs(감출 언어)로 잡은 이유 — 고객이 새 국가 계정을 연동했을 때 기존 블록이
 * 자동으로 모두 보여야 한다. 반대로 visible_langs(보일 언어)면 계정을 추가할 때마다 모든
 * 블록을 일일이 켜줘야 하고, 빠뜨리면 새 국가 페이지가 비어 보인다.
 */

export interface TranslatableField {
    key: string;
    label: string;
    multiline?: boolean;
    placeholder?: string;
}

/** 블록 타입별 번역 대상 필드 — 편집 폼과 미리보기가 공유한다 */
export const TRANSLATABLE_FIELDS: Record<string, TranslatableField[]> = {
    text: [
        { key: 'title', label: '제목' },
        { key: 'sub_title', label: '내용', multiline: true },
    ],
    link: [
        { key: 'title', label: '제목' },
        { key: 'link_url', label: '링크 주소', placeholder: '비우면 원본 주소를 그대로 사용합니다' },
    ],
    shopping: [
        { key: 'title', label: '제목' },
        { key: 'link_url', label: '링크 주소', placeholder: '비우면 원본 주소를 그대로 사용합니다' },
    ],
    image: [
        { key: 'title', label: '제목' },
        { key: 'link_url', label: '링크 주소', placeholder: '비우면 원본 주소를 그대로 사용합니다' },
    ],
    video: [{ key: 'title', label: '제목' }],
    partners: [
        { key: 'title', label: '제목' },
        { key: 'sub_title', label: '부제목' },
    ],
    collection: [{ key: 'title', label: '제목' }],
    calendar: [
        { key: 'title', label: '제목' },
        { key: 'sub_title', label: '부제목' },
    ],
    // 공동구매(공구) 블록의 제목 필드는 group_title 이다(title 아님)
    gonggu: [{ key: 'group_title', label: '제목' }],
    // gallery·line·social 은 이미지·아이콘만 있어 번역 대상이 없다
};

/**
 * 중첩 배열 항목의 번역 대상 — 평면 키 `<field>#<itemId>.<textKey>` 규약.
 * push(glorills.service.pushBlocksToGlorills)와 적용(applyLocaleToBlock·공개페이지
 * applyServerTranslation)이 이 표를 공유해, 배열 안 텍스트도 빠짐없이 번역·복원한다.
 *
 * spacehome(공개 페이지)의 동명 표와 항상 일치시킬 것.
 */
export interface ArrayTranslatableSpec {
    /** block_content 안의 배열 필드명 */
    field: string;
    /** 배열 항목의 고유 id 필드명 (번역을 원본 항목에 되돌릴 때 매칭 키) */
    idKey: string;
    /** 번역할 텍스트 키들 */
    textKeys: string[];
}
export const ARRAY_TRANSLATABLE_FIELDS: Record<string, ArrayTranslatableSpec[]> = {
    link: [{ field: 'badge_content', idKey: 'badge_id', textKeys: ['text'] }],
    image: [{ field: 'badge_content', idKey: 'badge_id', textKeys: ['text'] }],
    shopping: [{ field: 'badge_content', idKey: 'badge_id', textKeys: ['text'] }],
    schedule: [{ field: 'schedule_content', idKey: 'schedule_id', textKeys: ['title'] }],
    calendar: [{ field: 'items', idKey: 'id', textKeys: ['title', 'sub_title'] }],
    partners: [{ field: 'partners_content', idKey: 'partners_id', textKeys: ['title'] }],
    collection: [{ field: 'link_blocks', idKey: 'id', textKeys: ['title', 'description'] }],
};

/** 평면 키 파서 — `<field>#<itemId>.<textKey>` → {field,id,textKey} (매칭 실패 시 null) */
export function parseArrayFlatKey(
    key: string,
): { field: string; id: string; textKey: string } | null {
    const m = key.match(/^([a-zA-Z_]+)#(.+)\.([a-zA-Z_]+)$/);
    return m ? { field: m[1], id: m[2], textKey: m[3] } : null;
}

/** 이 블록 타입에 번역할 텍스트가 있는지 */
export function hasTranslatableFields(blockType: string): boolean {
    return (TRANSLATABLE_FIELDS[blockType]?.length ?? 0) > 0;
}

/** 원본 언어인지 — 원본은 번역이 아니라 블록의 기존 필드를 직접 편집한다 */
export function isBaseLocale(lang: string): boolean {
    return lang === BASE_LOCALE_CODE;
}

/** 해당 언어에서 이 블록을 감추는지 */
export function isBlockHiddenAt(block: any, lang: string): boolean {
    if (isBaseLocale(lang)) return false;
    const hidden = block?.block_content?.hidden_langs;
    return Array.isArray(hidden) && hidden.includes(lang);
}

/** 해당 언어의 번역값 묶음 (없으면 빈 객체) */
export function getTranslation(block: any, lang: string): Record<string, any> {
    if (isBaseLocale(lang)) return {};
    return block?.block_content?.i18n?.[lang] ?? {};
}

/** 특정 필드의 번역값 — 비어 있으면 null (호출부가 원본으로 폴백) */
export function getTranslatedField(block: any, lang: string, field: string): string | null {
    const value = getTranslation(block, lang)[field];
    return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * 블록에 번역을 입혀 미리보기용 데이터로 변환한다.
 * 번역이 비어 있는 필드는 원본을 그대로 남긴다 — 미번역 블록이 사라져 보이지 않게.
 *
 * 이 함수로 변환한 데이터를 그대로 기존 블록 컴포넌트에 넘기므로,
 * 개별 블록 렌더러(LinkBlock·TextBlock 등)는 수정할 필요가 없다.
 */
export function applyLocaleToBlock(block: any, lang: string): any {
    if (isBaseLocale(lang)) return block;
    const translation = getTranslation(block, lang);

    const filled: Record<string, any> = {};
    // 중첩 배열 항목 번역 — 평면 키 `<field>#<id>.<textKey>` 를 field→id→{key:값} 으로 모은다
    const arraySpecs = ARRAY_TRANSLATABLE_FIELDS[block?.block_type] ?? [];
    const specByField: Record<string, ArrayTranslatableSpec> = {};
    for (const s of arraySpecs) specByField[s.field] = s;
    const arrayEdits: Record<string, Record<string, Record<string, string>>> = {};

    for (const [key, value] of Object.entries(translation)) {
        if (value === null || value === undefined) continue;
        if (typeof value !== 'string' || value.trim() === '') continue;
        const parsed = parseArrayFlatKey(key);
        if (parsed && specByField[parsed.field]) {
            ((arrayEdits[parsed.field] ??= {})[parsed.id] ??= {})[parsed.textKey] = value;
        } else {
            filled[key] = value;
        }
    }

    const content = block?.block_content ?? {};
    for (const spec of arraySpecs) {
        const edits = arrayEdits[spec.field];
        if (edits && Array.isArray(content[spec.field])) {
            filled[spec.field] = content[spec.field].map((item: any) =>
                edits[item?.[spec.idKey]] ? { ...item, ...edits[item[spec.idKey]] } : item,
            );
        }
    }

    if (Object.keys(filled).length === 0) return block;
    return { ...block, block_content: { ...content, ...filled } };
}

/** 번역 대상 필드 중 몇 개가 채워졌는지 — "2/3 번역됨" 표시용 */
export function translationProgress(block: any, lang: string): { filled: number; total: number } {
    const fields = TRANSLATABLE_FIELDS[block?.block_type] ?? [];
    const translation = getTranslation(block, lang);
    const filled = fields.filter((f) => {
        const v = translation[f.key];
        return typeof v === 'string' && v.trim() !== '';
    }).length;
    return { filled, total: fields.length };
}
